import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash.get';

import { liveBlogCard } from 'lib/CustomPropTypes';
import { reduceCSL } from 'lib/arrayUtils';

import { getBodyItemComponent } from 'components/Article/getBodyItemComponent';
import InlineVideo from 'components/InlineVideo';
import SocialShareList from 'components/Social/ShareList';

import Thumbnail from 'components/Thumbnail';
import TimeAgo from 'components/TimeAgo';

import CardHeadline from 'components/Card/CardHeadline';
import AIMS_FLAVORS from 'lib/aimsFlavors';
import { getSiteConfigs } from 'lib/vertical';
import styles from './styles.module.scss';

const block = 'LiveBlogCard';

const cardMarkup = (
  markupAndEmbeds,
  amp,
  path,
) => markupAndEmbeds.map((markup, index) => getBodyItemComponent(markup, index, amp, path));

const byThumbnail = (thumbnail, label) => (
  <div className={classNames(styles.thumbnail, 'mt1 mr2')} data-test="thumbnail">
    <Thumbnail
      thumbnail={thumbnail}
      responsiveFlavors={{ s: AIMS_FLAVORS.FOCAL_100x100 }}
      label={label}
      additionalStyles={{
        raw: styles.thumbnail,
        thumbnail: styles.thumbnail,
      }}
    />
  </div>
);

const byline = (authors) => {
  if (!Array.isArray(authors) || !authors.length) {
    return null;
  }

  let authorName = '';

  if (authors.length === 1) {
    authorName = get(authors, '[0].name', '');
  } else {
    authorName = authors.map((author) => author.name).reduce(reduceCSL);
  }

  return (
    <p className="f4 founders-cond mv0 gray-80" data-test="byline">{authorName}</p>
  );
};

const bylineContainer = (authors, date) => {
  const { primaryImage } = (authors && authors[0]) || {};
  const oneAuthorOnly = authors && authors.length === 1;

  return (
    <div className={classNames('byline mb6 df', styles.byline)} data-test="byline-container">
      {
        oneAuthorOnly
        && primaryImage
        && primaryImage.url
        && byThumbnail(
          primaryImage.url.primary,
          primaryImage.altText,
        )
      }
      <div className="df flex-column">
        {authors && byline(authors)}
        {date && <TimeAgo dateString={date.modifiedAt} />}
      </div>
    </div>
  );
};


const Card = ({
  cardType,
  authors,
  content: { markupAndEmbeds },
  date,
  headline: { primary: primaryHed },
  isHeadlineHidden = false,
  id,
  isLastCard,
  path,
  canonicalUrl,
  url,
  cardData: videoData,
  newActiveItem,
}, { amp, vertical }) => {
  const cardClasses = classNames(
    block,
    'article-body__content',
    'w-100 mh0 mb8 fn overflow-x-hidden',
    styles.card,
    {
      [styles.newActiveItem]: newActiveItem,
      [styles.amp]: amp,
      [styles.lastCard]: isLastCard,
    },
  );

  let socialUrl = url.primary;
  if (canonicalUrl) {
    const [canonical, qs] = canonicalUrl.split('?');
    socialUrl = `${canonical}/${id}${qs ? `?${qs}` : ''}`;
  }

  const text = encodeURIComponent(primaryHed);

  const { twitterIntentVia: via } = getSiteConfigs(vertical);

  const socialProps = {
    twitterHref: `https://twitter.com/intent/tweet?text=${text}&via=${via}&url=${socialUrl}&original_referer=${socialUrl}`,
    facebookHref: `https://www.facebook.com/sharer/sharer.php?u=${socialUrl}`,
    emailHref: `mailto:?subject=${primaryHed}&body=${socialUrl}`,
    classes: 'df justify-start mt6 lh-none',
    tracking: 'mbt_navbar_socialShare',
  };

  return (
    <div
      className={cardClasses}
      key={id}
      // amp attributes
      id={id}
      data-sort-time={date.publishedAt}
      data-update-time={date.modifiedAt}
      data-test="card-container"
    >
      <div className={styles.stripePattern} data-test="stripes" />
      {bylineContainer(authors, date)}
      { !isHeadlineHidden && (
        <CardHeadline
          primaryHed={primaryHed}
          path={path}
          id={id}
        />
      )}
      {cardMarkup(markupAndEmbeds, amp, path)}
      {cardType === 'VIDEO' && <InlineVideo video={videoData} />}
      <SocialShareList {...socialProps} />
    </div>
  );
};


Card.propTypes = liveBlogCard;

Card.contextTypes = {
  amp: PropTypes.bool,
  vertical: PropTypes.string,
};

export default Card;
